import UserAvatar from '../../common/UserAvatar';
import { TablePagination } from '../../ui/table-pagination';
import { User, Eye, Edit, Ban, CheckCircle, Trash2, RotateCcw } from 'lucide-react';
import { getRoleBadge, formatRoleName } from './roleUtils';

export default function UsersTable({
  users,
  isLoading,
  onView,
  onEdit,
  onToggleStatus,
  onDelete,
  onRestore,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="p-12 text-center">
          <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No users found</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">User</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Role</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Joined</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-stone-50" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <UserAvatar src={user.profile_image} name={user.name} role={user.role} size="md" />
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <p className="text-xs text-slate-400 font-mono">{user.id?.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-700">{user.email}</p>
                      <p className="text-sm text-slate-500">{user.phone || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                        {formatRoleName(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_deleted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                          <Trash2 className="w-3 h-3" />
                          Deleted
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.is_active ? <CheckCircle className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => onView(user.id)}
                          className="p-2 hover:bg-blue-50 rounded-lg"
                          data-testid={`view-user-${user.id}`}
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                        {user.is_deleted ? (
                          <button
                            onClick={() => onRestore(user.id, user.name)}
                            className="p-2 hover:bg-green-50 rounded-lg"
                            data-testid={`restore-user-${user.id}`}
                            title="Restore User"
                          >
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => onEdit(user)}
                              className="p-2 hover:bg-stone-100 rounded-lg"
                              data-testid={`edit-user-${user.id}`}
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4 text-slate-600" />
                            </button>
                            <button
                              onClick={() => onToggleStatus(user.id, user.is_active, user.name)}
                              className="p-2 hover:bg-stone-100 rounded-lg"
                              data-testid={`toggle-user-${user.id}`}
                              title={user.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active
                                ? <Ban className="w-4 h-4 text-amber-500" />
                                : <CheckCircle className="w-4 h-4 text-green-500" />}
                            </button>
                            <button
                              onClick={() => onDelete(user.id)}
                              className="p-2 hover:bg-red-50 rounded-lg"
                              data-testid={`delete-user-${user.id}`}
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </>
      )}
    </div>
  );
}
